import React, { useState } from 'react';
import { getVersionDisplay, getFullVersionInfo, VERSION_INFO } from '@/config/version';
import { cn } from '@/lib/utils';

interface VersionDisplayProps {
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  showOnHover?: boolean;
  detailed?: boolean;
}

export const VersionDisplay: React.FC<VersionDisplayProps> = ({
  className,
  position = 'bottom-right',
  showOnHover = false,
  detailed = false
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4'
  };

  const handleClick = async () => {
    if (detailed) {
      const fullInfo = getFullVersionInfo();
      try {
        await navigator.clipboard.writeText(JSON.stringify(fullInfo, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.log('Version Info:', fullInfo);
      }
    }
  };

  const handleMouseEnter = () => {
    if (showOnHover || detailed) {
      setShowDetails(true);
    }
  };

  const handleMouseLeave = () => {
    if (showOnHover || detailed) {
      setShowDetails(false);
    }
  };

  return (
    <div
      className={cn(
        'fixed z-50 select-none',
        positionClasses[position],
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          'px-2 py-1 text-xs rounded transition-all duration-200',
          'bg-gray-100 text-gray-600 border border-gray-200',
          'hover:bg-gray-200 hover:text-gray-800',
          detailed && 'cursor-pointer hover:shadow-sm',
          copied && 'bg-green-100 text-green-700 border-green-200'
        )}
        onClick={handleClick}
        title={detailed ? 'Click to copy version info' : undefined}
      >
        {copied ? 'Copied!' : getVersionDisplay()}
      </div>

      {/* Detailed tooltip */}
      {(showDetails || showOnHover) && (
        <div
          className={cn(
            'absolute mb-2 p-2 text-xs rounded shadow-lg',
            'bg-gray-900 text-white border border-gray-700',
            'min-w-48 max-w-64',
            position.includes('bottom') ? 'bottom-full' : 'top-full',
            position.includes('right') ? 'right-0' : 'left-0'
          )}
        >
          <div className="space-y-1">
            <div><strong>Version:</strong> {VERSION_INFO.version}</div>
            <div><strong>Build:</strong> {new Date(VERSION_INFO.build).toLocaleString()}</div>
            <div><strong>Environment:</strong> {VERSION_INFO.environment}</div>
            {detailed && (
              <>
                <div className="border-t border-gray-600 pt-1 mt-1">
                  <div className="text-gray-300">Click to copy full info</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Simple version for footer/header use
export const InlineVersion: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <span className={cn('text-xs text-gray-500', className)}>
      {getVersionDisplay()}
    </span>
  );
};
